import { useState } from 'react'
import cx from 'classnames'
import { ActivityEvent } from 'components/ActivityEvent/Types'
import { observer } from 'mobx-react-lite'
import { ArticleFlat } from 'types'
import { BrainstormNextStep } from './NextActions/BrainstormNextStep'
import { ContentBriefNextStep } from './NextActions/ContentBriefNextStep'
import { OutlineNextStep } from './NextActions/OutlineNextStep'
import { CampaignNextStep } from './NextActions/CampaignNextStep'
import { NewGenerationNextStep } from './NextActions/NewGenerationNextStep'
import { AssigneeType, PropertyType } from 'api/clients/PropertyClient'
import { useProseInstance } from 'providers/ProseInstanceProvider'
import Styles from './AIAssistant.module.scss'
import { Skeleton } from 'components/Skeleton/Skeleton'
import { ActionCard } from 'components/DocRightRail/screens/AIAssistant/ActionCard'
import { Content } from 'components/DocRightRail/screens/AIAssistant/Card'
import { TimelineEventType } from 'screens/Activity/Types'
import { useLastEvent } from 'hooks/useLastEvent'
import { Shimmerer } from 'blaze-ui/Shimmerer'
import { computed } from 'mobx'
import { useAlmanacEditorState } from 'stores/EditorState'
import { ConditionalAssignedProperties } from 'components/ConditionalAssignedProperties/ConditionalAssignedProperties'

interface Props {
  article: ArticleFlat
  layerId: string
  event?: ActivityEvent
  loading: boolean
}

export const NextActionCard = observer(function NextActionCard({ article, layerId, event, loading }: Props) {
  const { currentViewRef } = useProseInstance()
  const [propertiesLoaded, setPropertiesLoaded] = useState(false)

  if (loading)
    return (
      <ActionCard>
        <div className={Styles.contentWrapper}>
          <Content withIcon>
            <Skeleton className={cx(Styles.skeleton, Styles.avatar)} paragraph={false} title={false} avatar active />
            <div className={Styles.contentWrapper}>
              <div>
                <Skeleton className={cx(Styles.skeleton, Styles.xl)} paragraph={false} active />
                <Skeleton className={cx(Styles.skeleton, Styles.md)} paragraph={false} active />
              </div>
            </div>
          </Content>
        </div>
      </ActionCard>
    )

  if (!event || event.type !== TimelineEventType.ArticleAIGeneratedEvent) return <NewGenerationNextStep />

  return (
    <ConditionalAssignedProperties
      view={currentViewRef.current}
      assignee={{
        id: article.id,
        type: AssigneeType.Article,
      }}
    >
      {({ assignedProperties, loadMore }) => {
        if (!propertiesLoaded) {
          loadMore()
          setPropertiesLoaded(true)
        }

        const isCampaignDoc = assignedProperties.some((ap) => ap.property.type === PropertyType.Campaign)

        switch (event.data.generation_type) {
          case 'brainstorm':
            return <BrainstormNextStep article={article} layerId={layerId} />
          case 'content_brief':
            return <ContentBriefNextStep article={article} layerId={layerId} />
          case 'outline':
            return <OutlineNextStep article={article} layerId={layerId} />
          default:
            if (isCampaignDoc) {
              return <CampaignNextStep />
            }
            return <NewGenerationNextStep />
        }
      }}
    </ConditionalAssignedProperties>
  )
})

interface ContainerProps {
  article: ArticleFlat
  layerId: string
}

export const NextActionCardContainer = ({ article, layerId }: ContainerProps) => {
  const { lastEvent, loading } = useLastEvent({ articleId: article.id, layerId })
  const editorState = useAlmanacEditorState()

  const containsAiSuggestionNode = computed(() =>
    editorState?.allNodeTypes.has(editorState.view.state.schema.nodes.ai_suggestion),
  ).get()
  return (
    <Shimmerer variant="secondary">
      <NextActionCard
        article={article}
        layerId={layerId}
        event={lastEvent}
        loading={loading || !!containsAiSuggestionNode}
      />
    </Shimmerer>
  )
}
